import { useEffect, useRef, useState } from "react";
import { ptBR } from "date-fns/locale";
import {
  AlignLeft,
  CalendarDays,
  Check,
  Circle,
  Clock,
  GripVertical,
  Loader2,
  Star,
  Trash2,
} from "lucide-react";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { accentBg } from "../../utils/accent";

const hoje = () => new Date().toISOString().slice(0, 10);
const amanha = () => new Date(Date.now() + 86400000).toISOString().slice(0, 10);
export const dataBR = (d) => (d ? d.split("-").reverse().join("/") : "");

const paraData = (iso) => {
  if (!iso) return undefined;
  const [a, m, d] = iso.split("-").map(Number);
  return new Date(a, m - 1, d);
};
const paraIso = (date) => {
  const m = `${date.getMonth() + 1}`.padStart(2, "0");
  const d = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${m}-${d}`;
};

const Chip = ({ children, onClick, testId, ativo }) => (
  <button
    type="button"
    onClick={onClick}
    className={`h-6 px-2 rounded-full border text-[10px] font-medium transition-colors ${
      ativo
        ? "border-transparent text-white"
        : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
    }`}
    style={ativo ? accentBg() : undefined}
    data-testid={testId}
  >
    {children}
  </button>
);

/** Formulário inline (estilo Google Tarefas): salva sozinho enquanto se digita. */
function TaskEditor({ t, onPatch, onFechar, podeIndentar, onIndent, onOutdent }) {
  const [titulo, setTitulo] = useState(t.title || "");
  const [detalhes, setDetalhes] = useState(t.details || "");
  const [estado, setEstado] = useState("idle"); // idle | saving | saved
  const [calendario, setCalendario] = useState(false);
  const timer = useRef(null);
  const pendente = useRef(null);
  const patchRef = useRef(onPatch);
  patchRef.current = onPatch;

  const gravar = async (campos) => {
    pendente.current = null;
    setEstado("saving");
    await patchRef.current(campos);
    setEstado("saved");
  };

  const agendar = (campos) => {
    pendente.current = { ...(pendente.current || {}), ...campos };
    setEstado("saving");
    clearTimeout(timer.current);
    timer.current = setTimeout(() => gravar(pendente.current), 700);
  };

  // ao fechar, o que estiver pendente vai para o servidor na hora
  useEffect(
    () => () => {
      clearTimeout(timer.current);
      if (pendente.current) patchRef.current(pendente.current);
    },
    [],
  );

  const mudarTitulo = (v) => {
    setTitulo(v);
    if (v.trim()) agendar({ title: v.trim() });
  };

  return (
    <div className="pb-3.5 pr-2 space-y-1" data-testid={`quick-task-editor-${t.id}`}>
      <input
        autoFocus
        value={titulo}
        onChange={(e) => mudarTitulo(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onFechar();
          }
          if (e.key === "Tab" && !e.shiftKey && podeIndentar && !t.parent_id) {
            e.preventDefault();
            onIndent();
          }
          if (e.key === "Tab" && e.shiftKey && t.parent_id) {
            e.preventDefault();
            onOutdent();
          }
        }}
        placeholder="Título da tarefa"
        className="w-full bg-transparent border-0 outline-none text-[14px] font-medium text-gray-900 placeholder:text-gray-300"
        data-testid={`quick-task-title-${t.id}`}
      />

      <div className="flex items-start gap-2.5 pt-1">
        <AlignLeft size={14} className="text-gray-300 mt-1.5 shrink-0" />
        <textarea
          value={detalhes}
          rows={1}
          onChange={(e) => {
            setDetalhes(e.target.value);
            agendar({ details: e.target.value });
            e.target.style.height = "auto";
            e.target.style.height = `${e.target.scrollHeight}px`;
          }}
          placeholder="Adicionar detalhes"
          className="flex-1 resize-none bg-transparent border-0 outline-none text-[13px] leading-relaxed text-gray-700 placeholder:text-gray-300 py-1"
          data-testid={`quick-task-details-${t.id}`}
        />
      </div>

      <div className="flex items-center gap-1.5 pt-0.5">
        <Chip
          onClick={() => gravar({ due_date: hoje() })}
          ativo={t.due_date === hoje()}
          testId={`quick-task-today-${t.id}`}
        >
          Hoje
        </Chip>
        <Chip
          onClick={() => gravar({ due_date: amanha() })}
          ativo={t.due_date === amanha()}
          testId={`quick-task-tomorrow-${t.id}`}
        >
          Amanhã
        </Chip>
        <Popover open={calendario} onOpenChange={setCalendario}>
          <PopoverTrigger asChild>
            <button
              type="button"
              title="Escolher data e hora"
              className={`h-6 px-2 rounded-full border text-[10px] font-medium inline-flex items-center gap-1 transition-colors ${
                t.due_date || t.due_time
                  ? "border-transparent text-white"
                  : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
              }`}
              style={t.due_date || t.due_time ? accentBg() : undefined}
              data-testid={`quick-task-calendar-${t.id}`}
            >
              <CalendarDays size={11} />
              {t.due_date ? dataBR(t.due_date) : ""}
              {t.due_time ? ` · ${t.due_time}` : ""}
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="z-[1850] w-auto p-0 rounded-xl"
            data-testid={`quick-task-calendar-popover-${t.id}`}
          >
            <Calendar
              mode="single"
              locale={ptBR}
              selected={paraData(t.due_date)}
              defaultMonth={paraData(t.due_date)}
              onSelect={(d) => d && gravar({ due_date: paraIso(d) })}
            />
            <div className="flex items-center gap-2 px-3 py-2.5 border-t border-gray-100">
              <Clock size={13} className="text-gray-400 shrink-0" />
              <input
                type="time"
                value={t.due_time || ""}
                onChange={(e) => gravar({ due_time: e.target.value || null })}
                className="h-8 flex-1 px-2 rounded-lg border border-gray-200 text-[12px] text-gray-700 outline-none focus:border-gray-400"
                data-testid={`quick-task-time-${t.id}`}
              />
              <button
                type="button"
                onClick={() => {
                  gravar({ due_date: null, due_time: null });
                  setCalendario(false);
                }}
                className="h-8 px-2.5 rounded-lg text-[11px] font-medium text-gray-500 hover:bg-gray-100"
                data-testid={`quick-task-clear-date-${t.id}`}
              >
                Sem data
              </button>
            </div>
          </PopoverContent>
        </Popover>

        <div className="flex-1" />

        <span className="text-[11px] text-gray-400 inline-flex items-center gap-1.5">
          {estado === "saving" ? (
            <>
              <Loader2 size={11} className="animate-spin" /> Salvando…
            </>
          ) : estado === "saved" ? (
            <>
              <Check size={11} className="text-emerald-600" /> Salvo
            </>
          ) : null}
        </span>
      </div>
    </div>
  );
}

/** Linha da tarefa: some para dar lugar ao formulário inline quando aberta. */
export default function TaskRow({
  t,
  aberto,
  atrasada,
  filha = false,
  onAbrir,
  onFechar,
  onToggle,
  onPatch,
  onExcluir,
  podeIndentar,
  onIndent,
  onOutdent,
  arrasto,
}) {
  return (
    <div
      draggable={!aberto}
      onDragStart={() => arrasto.iniciar(t.id)}
      onDragOver={(e) => {
        e.preventDefault();
        arrasto.sobre(t.id);
      }}
      onDragEnd={arrasto.terminar}
      className={`group relative rounded-lg mb-0.5 transition-colors ${filha ? "ml-7" : ""} ${
        aberto ? "bg-gray-50" : "hover:bg-gray-50"
      } ${arrasto.arrastando === t.id ? "opacity-40" : ""}`}
      data-testid={`quick-task-${t.id}`}
    >
      <div className="absolute right-2 top-2 z-10 flex items-center gap-0.5">
        <button
          type="button"
          onClick={() => onPatch({ starred: !t.starred })}
          title={t.starred ? "Remover importância" : "Marcar como importante"}
          className={`h-7 w-7 rounded-full flex items-center justify-center hover:bg-gray-100 transition-opacity ${
            t.starred ? "" : "text-gray-300 hover:text-amber-500"
          } ${aberto || t.starred ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
          style={t.starred ? { color: "#f59e0b" } : undefined}
          data-testid={`quick-task-star-${t.id}`}
        >
          <Star size={14} fill={t.starred ? "currentColor" : "none"} />
        </button>
        <button
          type="button"
          onClick={() => onExcluir(t)}
          title="Excluir tarefa"
          className={`h-7 w-7 rounded-full flex items-center justify-center text-gray-300 hover:bg-red-50 hover:text-red-600 transition-opacity ${
            aberto ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          }`}
          data-testid={`quick-task-delete-${t.id}`}
        >
          <Trash2 size={14} />
        </button>
      </div>

      <div className="flex items-start gap-2 px-2 py-2.5 pr-16">
        <span
          className={`mt-0.5 text-gray-200 cursor-grab active:cursor-grabbing transition-opacity ${
            aberto ? "opacity-0" : "opacity-0 group-hover:opacity-100"
          }`}
          data-testid={`quick-task-handle-${t.id}`}
        >
          <GripVertical size={14} />
        </span>
        <button
          type="button"
          onClick={() => onToggle(t)}
          className="mt-0.5 text-gray-300 hover:text-emerald-600 transition-colors shrink-0"
          aria-label={t.done ? "Reabrir tarefa" : "Concluir tarefa"}
          data-testid={`quick-task-toggle-${t.id}`}
        >
          {t.done ? <Check size={18} className="text-emerald-600" /> : <Circle size={18} />}
        </button>

        {aberto ? (
          <div className="flex-1 min-w-0">
            <TaskEditor
              t={t}
              onPatch={onPatch}
              onFechar={onFechar}
              podeIndentar={podeIndentar}
              onIndent={onIndent}
              onOutdent={onOutdent}
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onAbrir(t.id)}
            className="flex-1 text-left min-w-0"
            data-testid={`quick-task-expand-${t.id}`}
          >
            <p
              className={`text-[14px] leading-snug ${
                t.done ? "text-gray-400 line-through" : "text-gray-800"
              }`}
            >
              {t.title}
            </p>
            {(t.details || t.due_date) && (
              <div className="flex flex-wrap items-center gap-2.5 mt-1 text-[11px] text-gray-400">
                {t.details && <span className="truncate max-w-[280px]">{t.details}</span>}
                {t.due_date && (
                  <span
                    className={`inline-flex items-center gap-1 ${
                      atrasada ? "text-red-500 font-semibold" : ""
                    }`}
                  >
                    <CalendarDays size={11} /> {dataBR(t.due_date)}
                    {t.due_time ? ` · ${t.due_time}` : ""}
                  </span>
                )}
              </div>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
